import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { Card, Button, Spinner } from "@fluentui/react-components";
import { Search24Regular } from "@fluentui/react-icons";
import TextInputField from "components/controls/TextInputField";
import HookFormSelectField from "components/controls/HookFormSelectField";
import HookFormTextInputField from "components/controls/HookFormTextInputField";
import InputField from "components/controls/InputField";
import {
  getVehicleMarkOptions,
  getModelsByMark,
  VehicleOption,
} from "constants/getVehicleModels";
import { useSharedStyles } from "../styles/sharedStyles";
import api from "service/api";

type Props = {
  form: UseFormReturn<any, any>;
  isLoading: boolean;
};

const VehicleSection = ({ form, isLoading }: Props) => {
  const { t } = useTranslation();
  const classes = useSharedStyles();
  // State for dependent dropdowns
  const [brandOptions, setBrandOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const selectedBrand = form.watch("vehicle.brand");

  // State for VIN lookup
  const [isVinLookupLoading, setIsVinLookupLoading] = useState(false);
  const vinValue = form.watch("vehicle.vin");
  // Initialize brand options on component mount
  useEffect(() => {
    const vehicleBrandOptions = getVehicleMarkOptions();
    const formattedBrandOptions = vehicleBrandOptions.map(
      (option: VehicleOption) => ({
        value: option.key,
        label: option.text,
      })
    );

    // Sort brand options alphabetically by label
    formattedBrandOptions.sort((a, b) => a.label.localeCompare(b.label));

    setBrandOptions(formattedBrandOptions);
  }, []);

  // Update model options when brand changes
  useEffect(() => {
    if (selectedBrand) {
      const vehicleModelOptions = getModelsByMark(selectedBrand);
      const formattedModelOptions = vehicleModelOptions.map(
        (option: VehicleOption) => ({
          value: option.key,
          label: option.text,
        })
      );
      setModelOptions(formattedModelOptions);
    } else {
      // Reset model options if no brand is selected
      setModelOptions([{ value: "", label: "" }]);
      // Clear model field
      form.setValue("vehicle.model", "");
    }
  }, [selectedBrand, form]);

  // Define fuel type options
  const fuelOptions = [
    {
      value: "gasoline",
      label: t("ila.vehicle.fuel.gasoline"),
    },
    { value: "diesel", label: t("ila.vehicle.fuel.diesel") },
    {
      value: "electric",
      label: t("ila.vehicle.fuel.electric"),
    },
    { value: "hybrid", label: t("ila.vehicle.fuel.hybrid") },
    { value: "lpg", label: t("ila.vehicle.fuel.lpg") },
    { value: "other", label: t("ila.vehicle.fuel.other") },
  ];
  // Define vehicle type options
  const typeOptions = [
    { value: "A", label: t("ila.vehicle.type.A") }, // osobní automobil
    { value: "A1", label: t("ila.vehicle.type.A1") }, // obytný automobil
    { value: "A2", label: t("ila.vehicle.type.A2") }, // sanitní automobil
    { value: "B", label: t("ila.vehicle.type.B") }, // motocykl
    { value: "B1", label: t("ila.vehicle.type.B1") }, // tříkolka, čtyřkolka do 400 kg
    { value: "B2", label: t("ila.vehicle.type.B2") }, // tříkolka, čtyřkolka nad 400 kg
    { value: "C", label: t("ila.vehicle.type.C") }, // automobil nad 3500kg
    { value: "C1", label: t("ila.vehicle.type.C1") }, // nákladní automobil
    { value: "C2", label: t("ila.vehicle.type.C2") }, // traktor
    { value: "C3", label: t("ila.vehicle.type.C3") }, // pracovní stroj s rz/spz
    { value: "C4", label: t("ila.vehicle.type.C4") }, // tahač návěsů
    { value: "C5", label: t("ila.vehicle.type.C5") }, // pracovní stroj bez spz
    { value: "C6", label: t("ila.vehicle.type.C6") }, // nákladní vozidlo z modif.os.
    { value: "C7", label: t("ila.vehicle.type.C7") }, // ruční n. vysokozdvižný vozík
    { value: "C8", label: t("ila.vehicle.type.C8") }, // jednonápr. tr.,traktor bez rz
    { value: "D", label: t("ila.vehicle.type.D") }, // kolo s pomocným motorem
    { value: "E", label: t("ila.vehicle.type.E") }, // autobus
    { value: "E1", label: t("ila.vehicle.type.E1") }, // autobus k městské hrom.dopr.
    { value: "E2", label: t("ila.vehicle.type.E2") }, // trolejbus
    { value: "F", label: t("ila.vehicle.type.F") }, // přívěs
    { value: "F1", label: t("ila.vehicle.type.F1") }, // návěs, přívěs
    { value: "F2", label: t("ila.vehicle.type.F2") }, // návěs za kamion
    { value: "H1", label: t("ila.vehicle.type.H1") }, // elektrokoloběžka, segway aj.
    { value: "H2", label: t("ila.vehicle.type.H2") }, // zah. traktor, golf. vozík aj.
    { value: "H3", label: t("ila.vehicle.type.H3") }, // ostatní
  ]; // Define manufacture year options (current year to 10 years in the past)
  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: "", label: "" },
    ...Array.from({ length: 11 }, (_, index) => {
      const year = currentYear - index;
      return { value: year.toString(), label: year.toString() };
    }),
  ];
  const registrationTypeOptions = [
    { value: "0", label: t("ila.vehicle.registrationType.0") }, // nepodléhá registraci
    { value: "DC", label: t("ila.vehicle.registrationType.DC") }, // diplomatická/cizinecká
    { value: "EL", label: t("ila.vehicle.registrationType.EL") }, // pro vozidla na elektřinu nebo vodík
    { value: "HV", label: t("ila.vehicle.registrationType.HV") }, // pro historická vozidla
    { value: "JP", label: t("ila.vehicle.registrationType.JP") }, // z místa prodeje do místa registrace
    { value: "NP", label: t("ila.vehicle.registrationType.NP") }, // na přání
    { value: "S1", label: t("ila.vehicle.registrationType.S1") }, // pro silniční vozidla
    { value: "S2", label: t("ila.vehicle.registrationType.S2") }, // pro zvláštní vozidla
    { value: "SV", label: t("ila.vehicle.registrationType.SV") }, // pro sportovní vozidla
    { value: "TM", label: t("ila.vehicle.registrationType.TM") }, // pro manipulační provoz
    { value: "TV", label: t("ila.vehicle.registrationType.TV") }, // k trvalému vývozu do zahraničí
    { value: "ZU", label: t("ila.vehicle.registrationType.ZU") }, // pro zkušební účely
  ];
  // Define vehicle usage options
  const vehicleUsageOptions = [
    { value: "", label: "" },
    {
      value: "personal",
      label: t("ila.vehicle.usage.personal"),
    },
    {
      value: "business",
      label: t("ila.vehicle.usage.business"),
    },
    {
      value: "commercial",
      label: t("ila.vehicle.usage.commercial"),
    },
    {
      value: "rental",
      label: t("ila.vehicle.usage.rental"),
    },
    { value: "taxi", label: t("ila.vehicle.usage.taxi") },
    {
      value: "driving_school",
      label: t("ila.vehicle.usage.driving_school"),
    },
    {
      value: "emergency",
      label: t("ila.vehicle.usage.emergency"),
    },
    {
      value: "other",
      label: t("ila.vehicle.usage.other"),
    },
  ]; // VIN lookup function
  const handleVinLookup = async () => {
    if (!vinValue || vinValue.trim().length === 0) {
      return;
    }

    setIsVinLookupLoading(true);
    try {
      // Call the VIN lookup API
      const response = await api.getVehicleInfoByVin(vinValue.trim());

      if (response.status === 200 && response.data) {
        const vehicleData = response.data;

        // Helper function to normalize strings for comparison
        const normalizeString = (str: string): string => {
          return str
            .toLowerCase()
            .trim()
            .replace(/[\s\-_]/g, "") // Remove spaces, hyphens, underscores
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
        };

        // Map and populate vehicle fields based on API response
        if (vehicleData) {
          // First, set the brand if available
          let brandToSet: string | null = null;
          if (vehicleData.brand) {
            const matchingBrand = brandOptions.find(
              (option) =>
                normalizeString(option.label) ===
                  normalizeString(vehicleData.brand) ||
                normalizeString(option.value) ===
                  normalizeString(vehicleData.brand)
            );
            if (matchingBrand) {
              form.setValue("vehicle.brand", matchingBrand.value);
              brandToSet = matchingBrand.value;
            }
          }

          // Wait for model options to update if brand was set
          if (brandToSet && vehicleData.model) {
            const vehicleModelOptions = getModelsByMark(brandToSet);
            const formattedModelOptions = vehicleModelOptions.map(
              (option: VehicleOption) => ({
                value: option.key,
                label: option.text,
              })
            );
            setModelOptions(formattedModelOptions);

            const matchingModel = formattedModelOptions.find(
              (option) =>
                normalizeString(option.label) ===
                  normalizeString(vehicleData.model) ||
                normalizeString(option.value) ===
                  normalizeString(vehicleData.model)
            );
            if (matchingModel) {
              form.setValue("vehicle.model", matchingModel.value);
            }
          }

          // Map fuel type if available with normalized comparison
          if (vehicleData.fuel) {
            const normalizedFuel = normalizeString(vehicleData.fuel);
            let matchingFuel = fuelOptions.find(
              (option) =>
                normalizeString(option.label) === normalizedFuel ||
                normalizeString(option.value) === normalizedFuel
            );

            // Handle common fuel type mappings
            if (!matchingFuel) {
              if (
                normalizedFuel.includes("benzin") ||
                normalizedFuel.includes("gasoline") ||
                normalizedFuel.includes("petrol")
              ) {
                matchingFuel = fuelOptions.find(
                  (option) => option.value === "gasoline"
                );
              } else if (
                normalizedFuel.includes("nafta") ||
                normalizedFuel.includes("diesel")
              ) {
                matchingFuel = fuelOptions.find(
                  (option) => option.value === "diesel"
                );
              } else if (
                normalizedFuel.includes("electric") ||
                normalizedFuel.includes("elektro")
              ) {
                matchingFuel = fuelOptions.find(
                  (option) => option.value === "electric"
                );
              } else if (normalizedFuel.includes("hybrid")) {
                matchingFuel = fuelOptions.find(
                  (option) => option.value === "hybrid"
                );
              } else if (
                normalizedFuel.includes("lpg") ||
                normalizedFuel.includes("lng")
              ) {
                matchingFuel = fuelOptions.find(
                  (option) => option.value === "lpg"
                );
              }
            }

            if (matchingFuel) {
              form.setValue("vehicle.fuel", matchingFuel.value);
            }
          }

          // Map vehicle type if available with normalized comparison
          if (vehicleData.category) {
            const normalizedCategory = normalizeString(vehicleData.category);
            let matchingType = typeOptions.find(
              (option) =>
                normalizeString(option.label) === normalizedCategory ||
                normalizeString(option.value) === normalizedCategory
            );

            // Handle common type mappings
            if (!matchingType) {
              if (
                normalizedCategory.includes("car") ||
                normalizedCategory.includes("osobni") ||
                normalizedCategory.includes("auto")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "car"
                );
              } else if (
                normalizedCategory.includes("motorcycle") ||
                normalizedCategory.includes("motocykl")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "motorcycle"
                );
              } else if (
                normalizedCategory.includes("truck") ||
                normalizedCategory.includes("nakladni")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "truck"
                );
              } else if (
                normalizedCategory.includes("van") ||
                normalizedCategory.includes("dodavka")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "van"
                );
              } else if (
                normalizedCategory.includes("bus") ||
                normalizedCategory.includes("autobus")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "bus"
                );
              } else if (
                normalizedCategory.includes("trailer") ||
                normalizedCategory.includes("privez")
              ) {
                matchingType = typeOptions.find(
                  (option) => option.value === "trailer"
                );
              }
            }

            if (matchingType) {
              form.setValue("vehicle.type", matchingType.value);
            }
          }

          if (vehicleData.firstRegistrationDate) {
            const registrationYear = new Date(
              vehicleData.firstRegistrationDate
            ).getFullYear();
            form.setValue(
              "vehicle.manufactureYear",
              registrationYear.toString()
            );
          } else if (vehicleData.manufactureYear) {
            form.setValue(
              "vehicle.manufactureYear",
              vehicleData.manufactureYear.toString()
            );
          }

          if (vehicleData.registrationCertificateNumber) {
            form.setValue(
              "vehicle.registrationCertificateNumber",
              vehicleData.registrationCertificateNumber
            );
          }

          if (vehicleData.powerKw) {
            form.setValue("vehicle.powerKw", vehicleData.powerKw.toString());
          }

          if (vehicleData.cubicCapacityCcm) {
            form.setValue(
              "vehicle.cubicCapacityCcm",
              vehicleData.cubicCapacityCcm.toString()
            );
          }

          if (vehicleData.maximumGrossWeight) {
            form.setValue(
              "vehicle.maximumGrossWeight",
              vehicleData.maximumGrossWeight.toString()
            );
          }

          if (vehicleData.weight) {
            form.setValue("vehicle.weight", vehicleData.weight.toString());
          }

          if (vehicleData.seats) {
            form.setValue("vehicle.seats", vehicleData.seats.toString());
          }

          if (vehicleData.mileage) {
            form.setValue("vehicle.mileage", vehicleData.mileage.toString());
          }

          // Show success message
          console.log(
            "Vehicle information populated successfully from VIN lookup"
          );
        }
      }
    } catch (error: any) {
      console.error("Error during VIN lookup:", error);
      let errorMessage = "Unknown error occurred";

      if (error.response?.data?.Message) {
        errorMessage = error.response.data.Message;
      } else if (error.response?.statusText) {
        errorMessage = `VIN lookup failed: ${error.response.statusText}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // You could show a toast notification here
      alert(t("ila.vehicle.vin.lookup.error", { error: errorMessage }));
    } finally {
      setIsVinLookupLoading(false);
    }
  };

  return (
    <Card className={classes.card}>
      <div className={classes.section}>
        <div className={classes.grid}>
          <div className={classes.sectionTitle}>
            {t("ila.vehicleDetailsSection.title")}
          </div>
          {/* Row 1: VIN, License Plate, Registration Certificate Number */}
          <HookFormTextInputField
            label={t("ila.vehicle.vin.label")}
            name="vehicle.vin"
            autoComplete="on"
            required={true}
            disabled={isLoading}
            control={form.control}
            contentAfter={
              <>
                {isVinLookupLoading && <Spinner size="tiny" />}
                <Button
                  appearance="subtle"
                  icon={<Search24Regular />}
                  title={t("ila.vehicleDetailsSection.vinLookupTooltip")}
                  aria-label={t("ila.vehicleDetailsSection.vinLookupTooltip")}
                  onClick={handleVinLookup}
                  disabled={
                    isLoading || isVinLookupLoading || !vinValue?.trim()
                  }
                  size="small"
                />
              </>
            }
          />
          <HookFormTextInputField
            label={t("ila.vehicle.licensePlate.label")}
            name="vehicle.licensePlate"
            required={true}
            disabled={isLoading}
            control={form.control}
          />
          <HookFormTextInputField
            label={t("ila.vehicle.registrationCertificateNumber.label")}
            name="vehicle.registrationCertificateNumber"
            required={false}
            disabled={isLoading}
            control={form.control}
          />
          {/* Row 2: Brand, Model, Manufacture Year */}
          <HookFormSelectField
            label={t("ila.vehicle.brand.label")}
            name="vehicle.brand"
            form={form}
            options={brandOptions}
            required={true}
            disabled={isLoading}
          />
          <HookFormSelectField
            label={t("ila.vehicle.model.label")}
            name="vehicle.model"
            form={form}
            options={modelOptions}
            required={true}
            disabled={isLoading || !selectedBrand}
          />
          <HookFormSelectField
            label={t("ila.vehicle.manufactureYear.label")}
            name="vehicle.manufactureYear"
            form={form}
            options={yearOptions}
            required={true}
            disabled={isLoading}
          />
          {/* Row 3: Power (kW), Engine Displacement (ccm), Maximum Gross Weight (kg) */}
          <HookFormTextInputField
            label={t("ila.vehicle.powerKw.label")}
            name="vehicle.powerKw"
            required={false}
            disabled={isLoading}
            control={form.control}
            type="number"
          />
          <HookFormTextInputField
            label={t("ila.vehicle.cubicCapacityCcm.label")}
            name="vehicle.cubicCapacityCcm"
            required={false}
            disabled={isLoading}
            control={form.control}
            type="number"
          />
          <HookFormTextInputField
            label={t("ila.vehicle.maximumGrossWeight.label")}
            name="vehicle.maximumGrossWeight"
            required={false}
            disabled={isLoading}
            control={form.control}
            type="number"
          />
          {/* Row 4: Vehicle Type, Fuel Type, Number of Seats */}
          <HookFormSelectField
            label={t("ila.vehicle.type.label")}
            name="vehicle.vehicleCategory"
            form={form}
            options={typeOptions}
            required={true}
            disabled={isLoading}
          />
          <HookFormSelectField
            label={t("ila.vehicle.fuel.label")}
            name="vehicle.fuel"
            form={form}
            options={fuelOptions}
            required={false}
            disabled={isLoading}
          />
          <HookFormSelectField
            label={t("ila.vehicle.registrationType.label")}
            name="vehicle.registrationType"
            form={form}
            options={registrationTypeOptions}
            required={false}
            disabled={isLoading}
          />
          <HookFormTextInputField
            label={t("ila.vehicle.seats.label")}
            name="vehicle.seats"
            required={false}
            disabled={isLoading}
            control={form.control}
            type="number"
          />
          {/* Row 5: Vehicle Usage, Mileage */}
          <HookFormSelectField
            label={t("ila.vehicle.usage.label")}
            name="vehicle.vehicleUsage"
            form={form}
            options={vehicleUsageOptions}
            required={true}
            disabled={isLoading}
          />
          <HookFormTextInputField
            label={t("ila.vehicle.mileage.label")}
            name="vehicle.mileage"
            required={false}
            disabled={isLoading}
            control={form.control}
            type="number"
          />
          {/* Removed vehicle value field from this tab */}
        </div>
      </div>
    </Card>
  );
};

export default VehicleSection;
